import { Injectable, ElementRef } from '@angular/core';
import { VtrWidgetComponent } from './vtr-widget.component';
import { AwallComponentEvent } from '../../../models/awallComponentEvent';
import { CommunicationService } from '../../../services/communication.service';
import { ComponentEvent } from '../../../models/enums/componentEvent';

/**
 * This class is contains methods that help the VTR-Widget to use mock-videos simulating multiple video-connections
 *  for development purposes
 */
@Injectable()
export class MockVideoHelper {

  private vtrWidget: VtrWidgetComponent;
  private vtrWidgetRef: ElementRef;

  constructor(private communication: CommunicationService,
  ) { }

  public init(vtrWidget: VtrWidgetComponent, vtrWidgetRef: ElementRef) {
    this.vtrWidget = vtrWidget;
    this.vtrWidgetRef = vtrWidgetRef;
    this.mockConnect();
  }

  private mockConnect() {
    this.vtrWidget.connected = true;
    this.mockJoinVideoRoom();
  }

  private mockJoinVideoRoom() {
    // add mock-'local' video
    const localVideo = this.mockAddNewVideoToVideoContainer(this.vtrWidget.localVideoElementId, 'sample_person_01.mp4');
    this.vtrWidget.localVideoElement = localVideo;

    // add mock-remote videos
    const remote1 = this.mockAddNewVideoToVideoContainer('mockRemote1', 'sample_person_02.mp4');
    const remote2 = this.mockAddNewVideoToVideoContainer('mockRemote2', 'sample_person_03.mp4');
    const remote3 = this.mockAddNewVideoToVideoContainer('mockRemote3', 'sample_person_04.mp4');
    const remote4 = this.mockAddNewVideoToVideoContainer('mockRemote4', 'sample_person_05.mp4');
    const remote5 = this.mockAddNewVideoToVideoContainer('mockRemote5', 'sample_person_06.mp4');
    const remote6 = this.mockAddNewVideoToVideoContainer('mockRemote6', 'sample_person_07.mp4');
    const remote7 = this.mockAddNewVideoToVideoContainer('mockRemote7', 'sample_person_08.mp4');
    const remote8 = this.mockAddNewVideoToVideoContainer('mockRemote8', 'sample_person_09.mp4');
    //    const remote9 = this.mockAddNewVideoToVideoContainer('mockRemote9', 'sample_person_10.mp4');

    this.vtrWidget.remoteVideoElements.set('mockRemote1', remote1);
    this.vtrWidget.remoteVideoElements.set('mockRemote2', remote2);
    this.vtrWidget.remoteVideoElements.set('mockRemote3', remote3);
    this.vtrWidget.remoteVideoElements.set('mockRemote4', remote4);
    this.vtrWidget.remoteVideoElements.set('mockRemote5', remote5);
    this.vtrWidget.remoteVideoElements.set('mockRemote6', remote6);
    this.vtrWidget.remoteVideoElements.set('mockRemote7', remote7);
    this.vtrWidget.remoteVideoElements.set('mockRemote8', remote8);
    //  this.vtrWidget.remoteVideoElements.set('mockRemote9', remote9);
  }

  // autoplay pauses video when its not visible in viewport
  // play() allowes video to continue playing when not visible, but requires muted
  // see https://webkit.org/blog/6784/new-video-policies-for-ios/
  private mockAddNewVideoToVideoContainer(videoId: string, videoFileName: string) {
    const obj = this;
    const video = document.createElement('video');
    video.classList.add('videoelement');
    // video.autoplay = true;
    video.muted = true;
    video.controls = false;
    video.loop = true;
    video.src = 'assets/mockdata/mockvideos/' + videoFileName;
    video.load();
    video.setAttribute('id', videoId);
    video.onclick = function (event) {
      // if this is the second click of a double-click we dont react
      if (event.detail < 2) {
        obj.mockOnVideoElementClicked(event);
      }
    };
    video.ondblclick = function (event) {
      obj.mockOnVideoThumbnailDoubleclicked(event);
    };
    this.vtrWidget.getVideoContainer().appendChild(video);
    video.play();
    return video;
  }

  // Toggles fullscreen-mode for the clicked video
  private mockOnVideoElementClicked(event) {
    event.stopImmediatePropagation();
    const clickedVideoElement: HTMLVideoElement = <HTMLVideoElement>event.target;
    const videoIsFullscreen = this.vtrWidget.videoIsShownInFullscreen(clickedVideoElement);

    if (!videoIsFullscreen) {
      this.mockAddVideoToFullscreen(clickedVideoElement);
    }
    else {
      this.mockRemoveVideoFromFullscreen(clickedVideoElement);
    }
    this.vtrWidget.updateGridSize();
  }

  private mockOnVideoThumbnailDoubleclicked(event) {
    const allVideos = Array.from(this.vtrWidget.getVideoContainer().children);
    // if doubleclick on a video in fullscreen, we remove all, else we show all videos fullscreen
    const showAll = this.vtrWidget.videoIsShownInFullscreen(<HTMLVideoElement>event.target);

    allVideos.forEach(video => {
      const videoElement: HTMLVideoElement = <HTMLVideoElement>video;
      const videoIsFullscreen = this.vtrWidget.videoIsShownInFullscreen(videoElement);

      if (showAll && !videoIsFullscreen) {
        this.mockAddVideoToFullscreen(videoElement);
      }
      if (!showAll && videoIsFullscreen) {
        this.mockRemoveVideoFromFullscreen(videoElement);
      }
    });
    this.vtrWidget.updateGridSize();
  }

  private mockAddVideoToFullscreen(videoElement: HTMLVideoElement) {
    // Change border color of videoElement that is in fullscreenmode
    videoElement.classList.add('in-fullscreen');

    // Create CanvasElement on which fullscreen is displayed
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    canvas.id = videoElement.id;

    const container = this.vtrWidget.getFullscreenVideoContainer();
    container.appendChild(canvas);

    this.vtrWidget.backgroundVideoIsActive = true;
    this.communication.sendToAll(
      new AwallComponentEvent(ComponentEvent.WIDGET_VTR_VIDEO_ACTIVE, this.vtrWidget.backgroundVideoIsActive)
    );
    this.vtrWidget.startFullscreenUpdate(canvas, videoElement);
  }


  private mockRemoveVideoFromFullscreen(videoElement: HTMLVideoElement) {
    // Remove highlight-border from videoelement
    videoElement.classList.remove('in-fullscreen');

    // stop and destroy the updating function that draws on the fullscreen-canvas
    this.vtrWidget.stopFullscreenUpdate(videoElement);

    // remove all child-elements from the fullscreencontainer
    const fullscreenCanvas = this.vtrWidget.getFullscreenCanvasOfVideo(videoElement);
    fullscreenCanvas.remove();

    // Update state of fullscreenmode (maybe we just removed the last video from fullscreen and state changes to inactive)
    const fullscreenState = this.vtrWidget.getBackgroundVideoState();
    this.vtrWidget.backgroundVideoIsActive = fullscreenState;
    this.communication.sendToAll(
      new AwallComponentEvent(ComponentEvent.WIDGET_VTR_VIDEO_ACTIVE, this.vtrWidget.backgroundVideoIsActive)
    );
  }

}
